import { useState } from 'react';
import { Kpi, Pnl, Money, Pager, fmtUsd } from './anUi';
import { PositionsTable } from './Tables';

const PAGE_SIZE = 20;
const EMPTY = { resultat: 'Tous', dateFrom: '', dateTo: '', investMin: '', investMax: '', roiMin: '', roiMax: '', dureeMin: '', dureeMax: '' };

function Range({ label, a, b, filters, set, type = 'number' }) {
  return (
    <div className="an-field">
      <span className="an-field-label">{label}</span>
      <div className="an-field-pair">
        <input className="an-input an-input--sm" type={type} placeholder={type === 'date' ? '' : 'Min'} value={filters[a]} onChange={e => set(a, e.target.value)} />
        <input className="an-input an-input--sm" type={type} placeholder={type === 'date' ? '' : 'Max'} value={filters[b]} onChange={e => set(b, e.target.value)} />
      </div>
    </div>
  );
}

// Le Stats Explorer travaille sur les positions FERMÉES uniquement (les positions
// ouvertes n'ont ni ROI ni PNL réalisé : elles fausseraient ROI pondéré et PNL).
export default function StatsExplorerTab({ d }) {
  const [filters, setFilters] = useState(EMPTY);
  const [applied, setApplied] = useState(false);
  const [page, setPage] = useState(1);
  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const num = (v) => parseFloat(v);

  const closed = d.positions.filter(p => p.closed);
  const rows = applied ? closed.filter(p => {
    if (filters.resultat !== 'Tous' && p.resultat !== filters.resultat) return false;
    if (filters.dateFrom && p.entreeTs < new Date(filters.dateFrom).getTime() / 1000) return false;
    if (filters.dateTo && p.entreeTs > new Date(filters.dateTo).getTime() / 1000 + 86400) return false;
    if (filters.investMin !== '' && p.achatUsd < num(filters.investMin)) return false;
    if (filters.investMax !== '' && p.achatUsd > num(filters.investMax)) return false;
    if (filters.roiMin !== '' && p.roi < num(filters.roiMin)) return false;
    if (filters.roiMax !== '' && p.roi > num(filters.roiMax)) return false;
    if (filters.dureeMin !== '' && p.dureeMin < num(filters.dureeMin)) return false;
    if (filters.dureeMax !== '' && p.dureeMin > num(filters.dureeMax)) return false;
    return true;
  }) : [];

  const wins = rows.filter(p => p.resultat === 'Gagnant');
  const losses = rows.filter(p => p.resultat === 'Perdant');
  const winRate = rows.length ? (wins.length / rows.length) * 100 : null;
  const invest = rows.reduce((s, p) => s + p.achatUsd, 0);
  const pnl = rows.reduce((s, p) => s + p.pnlUsd, 0);
  const roiPondere = invest > 0 ? (pnl / invest) * 100 : null;
  const gainMoy = wins.length ? wins.reduce((s, p) => s + p.pnlUsd, 0) / wins.length : null;
  const perteMoy = losses.length ? Math.abs(losses.reduce((s, p) => s + p.pnlUsd, 0) / losses.length) : null;
  const totalPages = Math.ceil(rows.length / PAGE_SIZE);
  const paged = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="an-explorer">
      <div className="an-panel">
        <div className="an-section-title"><span className="an-section-icon">🧪</span>Filtres</div>
        <div className="an-field">
          <span className="an-field-label">Résultat</span>
          <div className="an-seg">
            {['Tous', 'Gagnant', 'Perdant'].map(r => (
              <button key={r} className={`an-filter-btn${filters.resultat === r ? ' an-filter-btn--active' : ''}`} onClick={() => set('resultat', r)}>{r}</button>
            ))}
          </div>
        </div>
        <Range label="Période" a="dateFrom" b="dateTo" filters={filters} set={set} type="date" />
        <Range label="Investi ($)" a="investMin" b="investMax" filters={filters} set={set} />
        <Range label="ROI (%)" a="roiMin" b="roiMax" filters={filters} set={set} />
        <Range label="Durée (min)" a="dureeMin" b="dureeMax" filters={filters} set={set} />
        <button className="an-btn an-btn--accent" onClick={() => { setApplied(true); setPage(1); }}>Appliquer</button>
        <button className="an-btn an-btn--ghost" onClick={() => { setFilters(EMPTY); setApplied(false); setPage(1); }}>Réinitialiser</button>
      </div>

      <div>
        {!applied ? (
          <div className="an-welcome">
            <div className="an-welcome-title">Stats Explorer</div>
            <div className="an-welcome-text">Explorez les conditions où ce wallet est le plus rentable. Les statistiques portent sur les positions fermées.</div>
            <div className="an-hint">Un win rate &gt; 70 % avec au moins 5 trades = pattern fort</div>
          </div>
        ) : (
          <div className="an-stack">
            <div className="an-kpis">
              <Kpi label="🔄 Trades" value={rows.length} />
              <Kpi label="✅ Gagnants" value={wins.length} tone="pos" />
              <Kpi label="❌ Perdants" value={losses.length} tone="neg" />
              <Kpi label="🎯 Win rate" value={winRate != null ? `${winRate.toFixed(1)}%` : '—'} tone={winRate != null ? (winRate >= 50 ? 'pos' : 'neg') : ''} />
              <Kpi label="📈 ROI pondéré" value={<Pnl value={roiPondere} />} tip="Σ PNL / Σ investi, positions fermées" />
              <Kpi label="💵 PNL total" value={<Money value={rows.length ? pnl : null} />} />
              <Kpi label="🟢 Gain moy" value={gainMoy != null ? fmtUsd(gainMoy) : '—'} tone="pos" />
              <Kpi label="🔴 Perte moy" value={perteMoy != null ? fmtUsd(perteMoy) : '—'} tone="neg" />
            </div>
            {rows.length === 0
              ? <p className="an-empty-inline">Aucune position ne correspond aux filtres.</p>
              : <>
                  <div className="an-muted an-small">{rows.length} position(s) · page {page}/{totalPages || 1}</div>
                  <PositionsTable rows={paged} />
                  <Pager page={page} total={totalPages} onPage={setPage} />
                </>}
          </div>
        )}
      </div>
    </div>
  );
}
